import './App.css';
import { type IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { ScopedCssBaseline, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useContext } from 'react';
import { RouterProvider } from 'react-router';

import { createGetIdTokenFunction } from './App.utils.js';
import { appRouter } from './appRouter';
import { ReactQueryDevtoolsForProduction } from './ReactQueryDevtools.js';
import { theme } from './theme/theme.js';
import { createConfiguredTrpcClient, queryClient, trpc } from './trpc';
import { useTriggerWindowFocus } from './useTriggerWindowFocus.js';
import { BackendConfigContext } from './utils';

type AppProps = {
  readonly msalInstance: IPublicClientApplication;
};

const App = ({ msalInstance }: AppProps) => {
  useTriggerWindowFocus();
  const backendConfig = useContext(BackendConfigContext);
  const idTokenFunction = createGetIdTokenFunction(msalInstance);
  const trpcClientInstance = createConfiguredTrpcClient(idTokenFunction, backendConfig.VivaPlanungBackendUrl);

  return (
    <MsalProvider instance={msalInstance}>
      <ThemeProvider theme={theme}>
        <ScopedCssBaseline>
          <trpc.Provider client={trpcClientInstance} queryClient={queryClient}>
            <QueryClientProvider client={queryClient}>
              <ReactQueryDevtools initialIsOpen={false} />
              <ReactQueryDevtoolsForProduction />
              <RouterProvider router={appRouter} />
            </QueryClientProvider>
          </trpc.Provider>
        </ScopedCssBaseline>
      </ThemeProvider>
    </MsalProvider>
  );
};

export default App;
